<template>
    <div>
        <template  v-if="Object.keys(spec_by_types).length > 0">
            <v-card color="grey lighten-4" class="mb-3" v-for="(spec_group, key, group_index) in spec_by_types" :key="group_index">
                <v-card-title style="padding-bottom:10px; padding-top:12px;" :style="$bp.smAndUp ? 'height:60px' : ''" class="white">
                    <div class="headline cursor-pointer mr-auto">{{ spec_group[0].spec_type }}</div>
                    <span v-if="profile.spec_type_id == key" class="fw-bold pr-3 fs-18">
                        {{ $t('lang.primary') }}
                    </span>
                    <v-btn
                        v-else-if="hasPerm('parts.update')"
                        class="mt-0"
                        color="primary"
                        @mouseup.left="update_loader ? null : updateSpecType(key)"
                    >
                        <v-icon v-if="update_loader && last_saved_type == key" color="white" class="ma-auto">{{ "fas fa-circle-notch fa-spin" }}</v-icon>
                        <span v-else>{{ $t('lang.set_prime') }}</span>
                    </v-btn>
                    <bc-reorder
                        v-if="spec_group && spec_group.length > 1"
                        :ref="`reorder_${key}`"
                        :items="spec_group"
                        primary-id="spec_id"
                        tooltip="reorder_specs"
                        perm="parts.update"
                        @save="saveSpecPositions($event, key)"
                    >
                        <template slot-scope="{ item }">
                            {{ item.item }}
                        </template>
                    </bc-reorder>
                    <bc-btn-add
                        style="width:50px;"
                        @invoke-method="showPartSpec(null, key)"
                        :tooltip="$t('lang.add_part_spec')"
                        perm="parts.update"
                    ></bc-btn-add>
                </v-card-title>
                <v-card-actions class="pa-0">
                    <v-data-table
                        :headers="headers"
                        :items="spec_group || []"
                        hide-actions
                        :no-data-text="$t('lang.no_data_available')"
                        class="w-100"
                        :class="{ 'prevent-click': !hasPerm('parts.update') }"
                    >
                        <template slot="headers" slot-scope="props">
                            <tr>
                                <th v-for="(header, index) in props.headers" :key="header.text" :class="[index == 0 ? ' pl-3 pr-2' : 'px-2', header.align == 'center' ? 'text-center' : 'text-xs-left']" class="column" :style="`width:${header.width}`">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </template>
                        <template slot="items" slot-scope="props">
                            <tr class="clickable" @mouseup.left="showPartSpec(props.item)">
                                <td class="pl-3 pr-2">{{ props.index + 1 }}</td>
                                <td class="px-2">{{ props.item.item }}</td>
                                <td class="px-2">{{ props.item.location }}</td>
                                <td class="px-2">{{ props.item.unit }}</td>
                                <td class="text-center px-2">{{ props.item.ll }}</td>
                                <td class="text-center px-2">{{ props.item.control_ll }}</td>
                                <td class="text-center px-2">{{ props.item.control_ul }}</td>
                                <td class="text-center px-2">{{ props.item.ul }}</td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card-actions>
            </v-card>
        </template>
        <div v-else class="blue-grey--text text--lighten-4 display-1" :style="$bp.smallScreen ? 'margin-top:16px;' : 'height:calc(80vh - 250px)'" style="width:100%; display: flex; justify-content: center; align-items: center;">{{ $t('lang.no_part_specs') }}</div>

        <bc-dialog :value="part_spec_dialog" max-width="800">
            <v-card>
                <v-card-title>
                    <span class="headline">{{ currSpecField('spec_id') ? $t('lang.edit_part_spec') : $t('lang.add_part_spec') }}</span>
                    <v-spacer></v-spacer>
                    <bc-btn-dialog-close
                        @invoke-method="invokeMethod($event)"
                        action="closePartSpecDialog"
                        :dialog-status="part_spec_dialog"
                    ></bc-btn-dialog-close>
                </v-card-title>
                <v-card-text>
                    <v-form ref="part_spec">
                        <v-container grid-list-form>
                            <v-layout row wrap>
                                <v-flex xs12 sm6 order-sm1>
                                    <bc-autocomplete
                                        v-model="part_spec.spec_type_id"
                                        :label="`${$t('lang.spec_type_id')} *`"
                                        :items="parts_spec_types"
                                        item-value="spec_type_id"
                                        item-text="spec_type"
                                        :rules="ifValid('part_spec.spec_type_id')"
                                    ></bc-autocomplete>
                                </v-flex>
                                <v-flex xs12 sm6 order-sm3>
                                    <v-text-field
                                        v-focus="part_spec_dialog"
                                        v-model="part_spec.item"
                                        :label="`${$t('lang.item')} *`"
                                        :counter="showCounter(part_spec.item, 'part_spec.item')"
                                        :rules="ifValid('part_spec.item')"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 order-sm5>
                                    <v-text-field
                                        v-model="part_spec.location"
                                        :label="$t('lang.location')"
                                        :counter="showCounter(part_spec.location, 'part_spec.location')"
                                        :rules="ifValid('part_spec.location')"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 order-sm7>
                                    <v-text-field
                                        v-model="part_spec.unit"
                                        :label="`${$t('lang.unit')} *`"
                                        :counter="showCounter(part_spec.unit, 'part_spec.unit')"
                                        :rules="ifValid('part_spec.unit')"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 order-sm2>
                                    <v-text-field
                                        v-model="part_spec.ll"
                                        :label="`${$t('lang.lower_limit')}`"
                                        :counter="showCounter(part_spec.ll, 'part_spec.ll')"
                                        :rules="ifValid('part_spec.ll')"
                                        type="number"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 order-sm4>
                                    <v-text-field
                                        v-model="part_spec.control_ll"
                                        :label="`${$t('lang.control_ll')} *`"
                                        :counter="showCounter(part_spec.control_ll, 'part_spec.control_ll')"
                                        :rules="ifValid('part_spec.control_ll')"
                                        type="number"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 order-sm6>
                                    <v-text-field
                                        v-model="part_spec.control_ul"
                                        :label="`${$t('lang.control_ul')} *`"
                                        :counter="showCounter(part_spec.control_ul, 'part_spec.control_ul')"
                                        :rules="ifValid('part_spec.control_ul')"
                                        type="number"
                                    ></v-text-field>
                                </v-flex>
                                <v-flex xs12 sm6 order-sm8>
                                    <v-text-field
                                        v-model="part_spec.ul"
                                        :label="`${$t('lang.upper_limit')}`"
                                        :counter="showCounter(part_spec.ul, 'part_spec.ul')"
                                        :rules="ifValid('part_spec.ul')"
                                        type="number"
                                    ></v-text-field>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn v-if="currSpecField('spec_id') && hasPerm('parts.delete')" color="error" @mouseup.left="$refs.delete_part_spec.openDialog()">{{ $t('lang.delete') }}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn flat @mouseup.left="closePartSpecDialog()">{{$t('lang.cancel')}}</v-btn>
                    <v-btn color="success" @mouseup.left="modifyPartSpec(currSpecField('spec_id') ? 'patch' : 'post', currSpecField('spec_id'))">{{$t('lang.save')}}</v-btn>
                </v-card-actions>
            </v-card>
        </bc-dialog>
        <delete-confirmation-dialog
            ref="delete_part_spec"
            :heading="$t('lang.delete_part_spec')"
            :message="$t('lang.confirm_delete_message')"
            @onConfirm="modifyPartSpec('delete', currSpecField('spec_id'))"
        ></delete-confirmation-dialog>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { store } from "Store/store";

export default {
    data() {
        return {
            headers: [
                { text: "#", sortable: false, width: "5%" },
                { text: this.$t("lang.item"), sortable: false, width: "20%" },
                { text: this.$t("lang.location"), sortable: false, width: "25%" },
                { text: this.$t("lang.unit"), sortable: false, width: "10%" },
                { text: this.$t("lang.lower_limit"), sortable: false, width: "10%", align: "center" },
                { text: this.$t("lang.control_ll_short"), sortable: false, width: "10%", align: "center" },
                { text: this.$t("lang.control_ul_short"), sortable: false, width: "10%", align: "center" },
                { text: this.$t("lang.upper_limit"), sortable: false, width: "10%", align: "center" },
            ],
            part_spec: {
                spec_type_id: "",
                item: "",
                location: "",
                ll: "",
                control_ll: "",
                control_ul: "",
                ul: "",
                unit: "",
            },
            current_spec: null,
            last_saved_type: null,
            part_spec_dialog: false,
            update_loader: false,
            spec_by_types: {},
        };
    },
    mounted() {
        if (this.parts_spec_types == null) this.getPartsSpecTypes();
        this.groupSpecsByTypes();
    },
    computed: {
        ...mapState({
            parts_spec: (state) => state.parts.parts_spec,
            rules_part_spec: (state) => state.validation.rules_part_spec,
            parts_spec_types: (state) => state.parts.parts_spec_types,
            profile: (state) => state.parts.part,
        }),
        currSpecField() {
            return (field) => {
                return this.current_spec && field && this.current_spec[field] ? this.current_spec[field] : "";
            };
        },
    },
    methods: {
        groupSpecsByTypes() {
            this.spec_by_types = _.groupBy(_.sortBy(this.parts_spec, ["spec_type_id", "sort_order"]), "spec_type_id");
        },
        saveSpecPositions(positions = null, spec_type = null) {
            if (positions && spec_type) {
                console.log(positions, spec_type);
                this.$store.commit("toggleModalLoader", true);
                this.$store
                    .dispatch("fetchPart", {
                        url: `parts/${this.$route.params.id}/spec_type/${spec_type}/position`,
                        method: "patch",
                        body: { spec_id: positions },
                    })
                    .then((data) => {
                        if (typeof data != "undefined") {
                            if (data.parts_spec) {
                                data.parts_spec.forEach((spec) => {
                                    this.$store.commit("mutatePart", {
                                        property: "parts_spec",
                                        pk: "spec_id",
                                        id: spec.spec_id,
                                        method: "patch",
                                        with: { sort_order: spec.sort_order },
                                    });
                                });
                            }
                            this.$refs[`reorder_${spec_type}`][0].close();
                            this.groupSpecsByTypes();
                        }
                        this.$store.commit("toggleModalLoader", false);
                    });
            }
        },
        getPartsSpecTypes() {
            this.$store.dispatch("fetchPart", {
                url: "parts_spec_types",
                method: "get",
                stateProperty: "parts_spec_types",
                commit: true,
            });
        },
        updateSpecType(spec_type = null) {
            if (spec_type && spec_type != this.profile.spec_type_id) {
                this.last_saved_type = spec_type;
                this.update_loader = true;
                this.$store
                    .dispatch("fetchPart", {
                        url: `parts/${this.$route.params.id}`,
                        method: "patch",
                        stateProperty: "part.spec_type_id",
                        dataProperty: "parts.spec_type_id",
                        commit: true,
                        body: {
                            spec_type_id: spec_type,
                        },
                    })
                    .then((data) => {
                        this.update_loader = false;
                        this.last_saved_type = null;
                    });
            }
        },
        closePartSpecDialog() {
            this.part_spec_dialog = false;
            if (this.$refs.delete_part_spec) this.$refs.delete_part_spec.close();
            this.$store.commit("validationOff");
            this.$store.commit("toggleModalLoader", false);
        },
        modifyPartSpec(method = null, id = null) {
            if (method) {
                this.$store.commit("validationOn");
                this.$nextTick(() => {
                    if (method == "delete" || this.validateForm("part_spec")) {
                        let is_first_spec = !this.parts_spec;
                        this.$store.commit("toggleModalLoader", true);
                        this.$store
                            .dispatch("fetchPart", {
                                pk: "spec_id",
                                id: id,
                                url: `parts/${this.$route.params.id}/spec/${id ? id : ""}`,
                                method: method,
                                stateProperty: "parts_spec",
                                mutate: true,
                                body: this.part_spec,
                            })
                            .then((data) => {
                                if (data && data.code == "200") {
                                    // if first spec for this part, update part's primary spec type
                                    if (is_first_spec) {
                                        let update_part = { ...this.profile };
                                        update_part.spec_type_id = this.part_spec.spec_type_id;
                                        this.$store.commit("commitPart", {
                                            property: "part",
                                            with: update_part,
                                        });
                                    }
                                    this.closePartSpecDialog();
                                    this.groupSpecsByTypes();
                                } else {
                                    this.$store.commit("toggleModalLoader", false);
                                }
                            });
                    }
                });
            }
        },
        showPartSpec(item = null, spec_type_id = null) {
            this.current_spec = item;

            if (this.$refs.part_spec) item ? this.$refs.part_spec.resetValidation() : this.$refs.part_spec.reset();

            this.$nextTick(() => {
                this.assignObj(this.part_spec, item, this.rules_part_spec);
                this.part_spec_dialog = true;
                if (spec_type_id) this.part_spec.spec_type_id = spec_type_id;
            });
        },
    },
};
</script>
